import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { withTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Feature, useFeature, useFeatures } from 'FeatureProvider';

import { useTheme } from '@mui/material/styles';
import { logger } from '@parrot-software/observability';
import { toast } from '@parrot-software/design-system';
import { Lightbulb, Sprout } from '@parrot-software/design-system/icons';

import { AppToaster } from 'components/AppToaster';

import { User } from '@parrot-software/design-system/icons';

import LogOutOutline from '@experimental-components/IconsComponents/LogOutOutline';
import IconHelp from '@experimental-components/IconsComponents/HelpPrincipalMenu';
import IconHeart from '@experimental-components/IconsComponents/Heart';
import IconAlert from '@experimental-components/IconsComponents/Alert';

import Reports from '@experimental-components/IconsComponents/PrincipalMenu/Reports';
import Menu from '@experimental-components/IconsComponents/PrincipalMenu/Menu';
import Users from '@experimental-components/IconsComponents/PrincipalMenu/Users';
import Invoice from '@experimental-components/IconsComponents/PrincipalMenu/Invoice';
import OnlineOrdering from '@experimental-components/IconsComponents/PrincipalMenu/OnlineOrdering';
import Settings from '@experimental-components/IconsComponents/PrincipalMenu/Settings';
import Inventories from '@experimental-components/IconsComponents/PrincipalMenu/Inventories';
import Subscription from '@experimental-components/IconsComponents/PrincipalMenu/Subscription';
import LineUpIcon from '@experimental-components/IconsComponents/PrincipalMenu/LineUp';

import SidebarLayout from 'components/SidebarLayout';

import { env } from 'config/env';

import { useTrinoSSO } from 'hooks/useTrinoSSO';
import { useGrowSSO } from 'hooks/useGrowSSO';

import useUserStore from 'hooks/useUserStore';
import useViewPanel from 'hooks/useViewPanel';
import useContentLayout from 'hooks/useContentLayout';
import useEnvironment from 'hooks/useEnvironment';
import { useInventoryModuleAccess } from 'hooks/inventories/useInventoryModuleAccess';
import useLicenseState from 'hooks/useLicenseState';
import useOrganization from 'hooks/Organization/useOrganization';
import { useSidebarLayout } from 'providers/root/SidebarLayoutProvider';

import { lastPaymentStatus } from 'utils/subscription';
import { getValidateItemsAvailabilityFlags } from 'utils/featureFlags/menus';
import { useGetEnableGeneralFlag, useGetEnabledDataFlag } from 'utils/featureFlags/reports';
import { MENU_ROUTES, MENU_SCOPE } from 'utils/menuMaker/constants';
import { useMenuScopeManagement, useMenuScopeIdentifier, useMenuScopeCleanup } from 'hooks/menuScope';

const growLog = logger.forDomain('IAM');

export function AppFrame({ children, t }) {
  const theme = useTheme();
  const location = useLocation();
  const { environmentsState } = useEnvironment();
  const { paymentStatus, paymentInvalid, licenseInvalid } = useLicenseState();
  const { openDrawer, openMdDrawer, isPublishMenuError } = useSidebarLayout();
  // Single source of truth for the "Suscripción" menu alert: both the red dot on the icon
  // and the complement IconAlert must always appear together, in both menu branches.
  const hasSubscriptionAlert =
    paymentStatus?.lastPaymentStatus === lastPaymentStatus.FAILED || paymentInvalid || licenseInvalid;
  const hasOrganizationAccess = useFeature('organizationAccess');
  const superUserAccess = useFeature('adminStore');
  // Read into constants instead of inlined in the `&&`: short-circuiting would make the second
  // useFeature call conditional, which rules-of-hooks rejects.
  const hasGrowModule = useFeature('@module.grow');
  const isOrganizationAdmin = useFeature('organizationAdmin');
  const { flags } = useFeatures();
  const flagEnableLineUp = useGetEnableGeneralFlag('ffEnableLineUp');
  const lineUpDetails = useGetEnabledDataFlag('ffEnableLineUp');
  const { organization } = useOrganization();
  const usersV3 = organization?.internalPermissionsSystem || false;

  const { enableValidateItemsAvailability } = getValidateItemsAvailabilityFlags(flags);

  const {
    user,
    storeState: { selected: storeSelected },
  } = useUserStore();

  const { setOpen } = useViewPanel();
  const { setValue } = useContentLayout();
  const [changeColorIcon, setChangeColorIcon] = useState(null);
  const [isTrinoLoading, setIsTrinoLoading] = useState(false);
  const [isGrowLoading, setIsGrowLoading] = useState(false);
  const { selectedScope } = useMenuScopeManagement();
  const { clearScopeSelection } = useMenuScopeCleanup();
  const ssoTrino = useTrinoSSO();
  const ssoGrow = useGrowSSO();

  // Same gate as the /inventories route, so the item never links to a redirect.
  const { isModuleEnabled: flagEnableInventory } = useInventoryModuleAccess();
  const flagEnableOnlineOrdering =
    useFeature('@module.onlineOrdering') && environmentsState.REACT_APP_FEATURE_ONLINE_ORDERING_ENABLED === 'true';

  function handleResetViewPanel() {
    setOpen(false);
    setValue(0);
  }

  // Block the <a> default navigation so we can run the SSO flow and open the
  // signed URL instead. ctrl/cmd+click still bypasses onClick and navigates
  // directly to Trino, where the middleware handles unauthenticated users.
  const handleTrinoClick = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (isTrinoLoading) return;
    setIsTrinoLoading(true);
    try {
      await ssoTrino({ target: 'new-tab' });
    } catch {
      toast.error(t('common:nav.ideasError'));
    } finally {
      setIsTrinoLoading(false);
    }
  };

  // The href is only the ctrl/cmd+click fallback; the click itself does the SSO handoff.
  const handleGrowClick = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (isGrowLoading) return;
    setIsGrowLoading(true);
    try {
      await ssoGrow({ target: 'new-tab' });
    } catch {
      toast.error(t('common:nav.growError'));
    } finally {
      setIsGrowLoading(false);
    }
  };

  // Sin `preventDefault`: acá el href SÍ es el destino, no el respaldo del ctrl+click.
  // Se mide porque es el único dato que va a decir si el upsell sirvió.
  const handleGrowUpsellClick = () => {
    growLog.info('growUpsellClick', { 'organization.uuid': organization?.uuid });
  };

  // Clear scope when navigating to non-menu sections
  useEffect(() => {
    // Only clear scope when navigating to specific non-menu sections
    // This ensures scope selection is shown when coming back from other sections
    const isNonMenuSection =
      location.pathname.startsWith('/reports') ||
      location.pathname.startsWith('/settings') ||
      location.pathname.startsWith('/workforce') ||
      location.pathname.startsWith('/employees') ||
      location.pathname.startsWith('/roles') ||
      location.pathname.startsWith('/inventories') ||
      location.pathname.startsWith('/online-ordering') ||
      location.pathname.startsWith('/invoicing') ||
      location.pathname.startsWith('/subscription');

    if (selectedScope && isNonMenuSection) {
      clearScopeSelection();
    }
  }, [location.pathname, selectedScope, clearScopeSelection]);

  const handleMouseEnter = (item) => {
    setChangeColorIcon((state) => ({ ...state, [item]: true }));
  };
  const handleMouseLeave = (item) => {
    setChangeColorIcon((state) => ({ ...state, [item]: false }));
  };

  // Workforce/Employees path logic based on usersV3 flag
  const employeePath = usersV3 ? '/employees' : '/workforce';
  const isInEmployeesSection = usersV3
    ? location.pathname.startsWith('/employees') || location.pathname.startsWith('/roles')
    : location.pathname.startsWith('/workforce');

  const isMenuRoute = (pathname) => {
    return pathname?.startsWith(MENU_ROUTES.ROOT);
  };

  // A session with no name still has a profile, so the row keeps its place and says what it leads to.
  const signedInName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
  const profileLabel = signedInName || t('common:nav.profile');

  // Dos modos, un solo item: un admin sin Grow ve la puerta al producto en vez de no ver
  // nada. El modo lo decide la ORGANIZACION, no la sucursal: por sucursal, un admin parado
  // en una sin Grow vería "conoce" algo que su organización ya paga.
  function renderGrowLink() {
    if (!isOrganizationAdmin) return null;

    const shared = {
      onMouseEnter: () => handleMouseEnter('grow'),
      onMouseLeave: () => handleMouseLeave('grow'),
      openMenu: openMdDrawer || openDrawer,
      startIcon: (
        <Sprout color={changeColorIcon?.grow ? theme.palette.secondary.main : theme.palette.common.white} size={16} />
      ),
    };

    if (!hasGrowModule) {
      return (
        <SidebarLayout.ExternalLinkItem
          {...shared}
          label={t('common:nav.growUpsell')}
          link={env.GROW_LANDING_URL}
          onClick={handleGrowUpsellClick}
          title={t('common:nav.growUpsellTooltip')}
        />
      );
    }

    return (
      <SidebarLayout.ExternalLinkItem
        {...shared}
        label={isGrowLoading ? t('common:nav.growLoading') : t('common:nav.grow')}
        link={env.GROW_BASE_URL}
        onClick={handleGrowClick}
        title={t('common:nav.growTooltip')}
      />
    );
  }

  function renderMenuSuperUser() {
    return (
      <>
        {!storeSelected?.isDraft && (
          <SidebarLayout.Item
            exact={false}
            isActive={(match, locationRef) => {
              return locationRef.pathname.startsWith('/reports');
            }}
            label={t('common:nav.reports')}
            onClick={handleResetViewPanel}
            onMouseEnter={() => {
              if (!location.pathname.startsWith('/reports')) handleMouseEnter('reports');
            }}
            onMouseLeave={() => {
              if (!location.pathname.startsWith('/reports')) handleMouseLeave('reports');
            }}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <Reports
                color={changeColorIcon?.reports ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            to="/reports"
          />
        )}

        <SidebarLayout.Item
          complementIcon={enableValidateItemsAvailability && isPublishMenuError ? <IconAlert size={20} /> : null}
          exact={false}
          isActive={(match, locationRef) => {
            return isMenuRoute(locationRef?.pathname);
          }}
          label={t('common:nav.menu')}
          onClick={handleResetViewPanel}
          onMouseEnter={() => {
            if (!isMenuRoute(location?.pathname)) handleMouseEnter('menu');
          }}
          onMouseLeave={() => {
            if (!isMenuRoute(location?.pathname)) handleMouseLeave('menu');
          }}
          openMenu={openMdDrawer || openDrawer}
          startIcon={
            <Menu
              alert={enableValidateItemsAvailability && isPublishMenuError}
              color={changeColorIcon?.menu ? theme.palette.secondary.main : theme.palette.common.white}
              size={16}
            />
          }
          to={MENU_ROUTES.ROOT}
        />

        {!storeSelected?.isDraft && (
          <>
            <SidebarLayout.Item
              exact={false}
              label={t('common:nav.setting')}
              onClick={handleResetViewPanel}
              onMouseEnter={() => {
                if (!location.pathname.startsWith('/settings')) handleMouseEnter('settings');
              }}
              onMouseLeave={() => {
                if (!location.pathname.startsWith('/settings')) handleMouseLeave('settings');
              }}
              openMenu={openMdDrawer || openDrawer}
              startIcon={
                <Settings
                  color={changeColorIcon?.settings ? theme.palette.secondary.main : theme.palette.common.white}
                  size={16}
                />
              }
              to="/settings"
            />
            <SidebarLayout.Item
              exact={false}
              isActive={(match, locationRef) => isInEmployeesSection}
              label={t('common:nav.personal')}
              onClick={handleResetViewPanel}
              onMouseEnter={() => {
                if (!isInEmployeesSection) handleMouseEnter('users');
              }}
              onMouseLeave={() => {
                if (!isInEmployeesSection) handleMouseLeave('users');
              }}
              openMenu={openMdDrawer || openDrawer}
              startIcon={
                <Users
                  color={changeColorIcon?.users ? theme.palette.secondary.main : theme.palette.common.white}
                  size={16}
                />
              }
              to={employeePath}
            />
            {/* @flag inventories */}
            {flagEnableInventory && (
              <SidebarLayout.Item
                exact={false}
                label={t('common:nav.inventories')}
                onClick={handleResetViewPanel}
                onMouseEnter={() => {
                  if (!location.pathname.startsWith('/inventories')) handleMouseEnter('inventories');
                }}
                onMouseLeave={() => {
                  if (!location.pathname.startsWith('/inventories')) handleMouseLeave('inventories');
                }}
                openMenu={openMdDrawer || openDrawer}
                startIcon={
                  <Inventories
                    color={changeColorIcon?.inventories ? theme.palette.secondary.main : theme.palette.common.white}
                    size={16}
                  />
                }
                to="/inventories"
              />
            )}
            {!storeSelected.isDraft && flagEnableOnlineOrdering && (
              <SidebarLayout.Item
                exact={false}
                label={t('common:nav.digitalMenu')}
                onClick={handleResetViewPanel}
                onMouseEnter={() => {
                  if (!location.pathname.startsWith('/online-ordering')) handleMouseEnter('online-ordering');
                }}
                onMouseLeave={() => {
                  if (!location.pathname.startsWith('/online-ordering')) handleMouseLeave('online-ordering');
                }}
                openMenu={openMdDrawer || openDrawer}
                startIcon={
                  <OnlineOrdering
                    color={
                      changeColorIcon?.['online-ordering'] ? theme.palette.secondary.main : theme.palette.common.white
                    }
                    size={16}
                  />
                }
                to="/online-ordering"
              />
            )}
            <SidebarLayout.Item
              exact={false}
              label={t('common:nav.invoicing')}
              onClick={handleResetViewPanel}
              onMouseEnter={() => {
                if (!location.pathname.startsWith('/invoicing')) handleMouseEnter('invoice');
              }}
              onMouseLeave={() => {
                if (!location.pathname.startsWith('/invoicing')) handleMouseLeave('invoice');
              }}
              openMenu={openMdDrawer || openDrawer}
              startIcon={
                <Invoice
                  color={changeColorIcon?.invoice ? theme.palette.secondary.main : theme.palette.common.white}
                  size={16}
                />
              }
              to="/invoicing"
            />
          </>
        )}
        {flagEnableLineUp && (
          <SidebarLayout.ExternalLinkItem
            label={lineUpDetails?.label}
            link={`${lineUpDetails?.url}${storeSelected?.uuid}`}
            openMenu={openMdDrawer || openDrawer}
            startIcon={<LineUpIcon />}
          />
        )}
        {renderGrowLink()}

        <SidebarLayout.Item
          complementIcon={hasSubscriptionAlert ? <IconAlert size={20} /> : null}
          exact={false}
          label={t('common:nav.subscription')}
          onClick={handleResetViewPanel}
          onMouseEnter={() => {
            if (!location.pathname.startsWith('/subscription')) handleMouseEnter('subscription');
          }}
          onMouseLeave={() => {
            if (!location.pathname.startsWith('/subscription')) handleMouseLeave('subscription');
          }}
          openMenu={openMdDrawer || openDrawer}
          startIcon={
            <Subscription
              alert={hasSubscriptionAlert}
              color={changeColorIcon?.subscription ? theme.palette.secondary.main : theme.palette.common.white}
              size={16}
            />
          }
          to="/subscriptions"
        />
      </>
    );
  }

  function renderMenuWithPermissions() {
    return (
      <>
        <Feature name="backofficeModuleReports">
          <SidebarLayout.Item
            exact={false}
            label={t('common:nav.reports')}
            onClick={handleResetViewPanel}
            onMouseEnter={() => {
              if (!location.pathname.startsWith('/reports')) handleMouseEnter('reports');
            }}
            onMouseLeave={() => {
              if (!location.pathname.startsWith('/reports')) handleMouseLeave('reports');
            }}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <Reports
                color={changeColorIcon?.reports ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            to="/reports"
          />
        </Feature>
        <Feature name="backofficeModuleMenu">
          <SidebarLayout.Item
            complementIcon={enableValidateItemsAvailability && isPublishMenuError ? <IconAlert size={20} /> : null}
            exact={false}
            label={t('common:nav.menu')}
            onClick={handleResetViewPanel}
            onMouseEnter={() => {
              if (!isMenuRoute(location?.pathname)) handleMouseEnter('menu');
            }}
            onMouseLeave={() => {
              if (!isMenuRoute(location?.pathname)) handleMouseLeave('menu');
            }}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <Menu
                alert={enableValidateItemsAvailability && isPublishMenuError}
                color={changeColorIcon?.menu ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            to={MENU_ROUTES.RESUME}
          />
        </Feature>
        <Feature name="backofficeModuleSettings">
          <SidebarLayout.Item
            exact={false}
            label={t('common:nav.setting')}
            onClick={handleResetViewPanel}
            onMouseEnter={() => {
              if (!location.pathname.startsWith('/settings')) handleMouseEnter('settings');
            }}
            onMouseLeave={() => {
              if (!location.pathname.startsWith('/settings')) handleMouseLeave('settings');
            }}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <Settings
                color={changeColorIcon?.settings ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            to="/settings"
          />
        </Feature>
        <Feature name="backofficeModuleWorkforce">
          <SidebarLayout.Item
            exact={false}
            isActive={(match, locationRef) => isInEmployeesSection}
            label={t('common:nav.personal')}
            onClick={handleResetViewPanel}
            onMouseEnter={() => {
              if (!isInEmployeesSection) handleMouseEnter('users');
            }}
            onMouseLeave={() => {
              if (!isInEmployeesSection) handleMouseLeave('users');
            }}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <Users
                color={changeColorIcon?.users ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            to={employeePath}
          />
        </Feature>
        <Feature name="backofficeModuleInvoicing">
          <SidebarLayout.Item
            exact={false}
            label={t('common:nav.invoicing')}
            onClick={handleResetViewPanel}
            onMouseEnter={() => {
              if (!location.pathname.startsWith('/invoicing')) handleMouseEnter('invoice');
            }}
            onMouseLeave={() => {
              if (!location.pathname.startsWith('/invoicing')) handleMouseLeave('invoice');
            }}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <Invoice
                color={changeColorIcon?.invoice ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            to="/invoicing"
          />
        </Feature>
        {/* @flag inventories */}
        {flagEnableInventory && (
          <SidebarLayout.Item
            exact={false}
            label={t('common:nav.inventories')}
            onClick={handleResetViewPanel}
            onMouseEnter={() => {
              if (!location.pathname.startsWith('/inventories')) handleMouseEnter('inventories');
            }}
            onMouseLeave={() => {
              if (!location.pathname.startsWith('/inventories')) handleMouseLeave('inventories');
            }}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <Inventories
                color={changeColorIcon?.inventories ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            to="/inventories"
          />
        )}
        {flagEnableLineUp && (
          <SidebarLayout.ExternalLinkItem
            label={lineUpDetails?.label}
            link={`${lineUpDetails?.url}${storeSelected?.uuid}`}
            openMenu={openMdDrawer || openDrawer}
            startIcon={<LineUpIcon />}
          />
        )}
        {renderGrowLink()}
        <Feature name="backofficeModuleSubscriptions">
          <SidebarLayout.Item
            complementIcon={hasSubscriptionAlert ? <IconAlert size={20} /> : null}
            exact={false}
            label={t('common:nav.subscription')}
            onClick={handleResetViewPanel}
            onMouseEnter={() => {
              if (!location.pathname.startsWith('/subscription')) handleMouseEnter('subscription');
            }}
            onMouseLeave={() => {
              if (!location.pathname.startsWith('/subscription')) handleMouseLeave('subscription');
            }}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <Subscription
                alert={hasSubscriptionAlert}
                color={changeColorIcon?.subscription ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            to="/subscriptions"
          />
        </Feature>
      </>
    );
  }

  return (
    <SidebarLayout>
      <SidebarLayout.Sidebar window={window}>
        <SidebarLayout.Section>
          {superUserAccess || hasOrganizationAccess ? renderMenuSuperUser() : renderMenuWithPermissions()}
        </SidebarLayout.Section>

        <SidebarLayout.Divider variant="middle" />

        <SidebarLayout.Section>
          <SidebarLayout.ItemMenu
            label={t('common:help.title')}
            onMouseEnter={() => handleMouseEnter('help')}
            onMouseLeave={() => handleMouseLeave('help')}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <IconHelp
                color={changeColorIcon?.help ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            t={t}
          />
          <SidebarLayout.Item
            label={t('common:nav.referral')}
            onMouseEnter={() => handleMouseEnter('referral')}
            onMouseLeave={() => handleMouseLeave('referral')}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <IconHeart
                color={changeColorIcon?.referral ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            to="/referral"
          />

          <SidebarLayout.Item
            label={profileLabel}
            onClick={handleResetViewPanel}
            onMouseEnter={() => handleMouseEnter('profile')}
            onMouseLeave={() => handleMouseLeave('profile')}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <User
                color={changeColorIcon?.profile ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            to="/profile"
          />

          <SidebarLayout.Item
            label={t('common:auth.signOut')}
            onClick={handleResetViewPanel}
            onMouseEnter={() => handleMouseEnter('logout')}
            onMouseLeave={() => handleMouseLeave('logout')}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <LogOutOutline
                color={changeColorIcon?.logout ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            to="/signout"
          />
        </SidebarLayout.Section>
        <SidebarLayout.Section
          css={{
            justifyContent: 'end',
            height: '100%',
          }}
        >
          <SidebarLayout.ExternalLinkItem
            label={isTrinoLoading ? t('common:nav.ideasLoading') : t('common:nav.ideas')}
            link={env.TRINO_BASE_URL}
            onClick={handleTrinoClick}
            onMouseEnter={() => handleMouseEnter('ideas')}
            onMouseLeave={() => handleMouseLeave('ideas')}
            openMenu={openMdDrawer || openDrawer}
            startIcon={
              <Lightbulb
                color={changeColorIcon?.ideas ? theme.palette.secondary.main : theme.palette.common.white}
                size={16}
              />
            }
            title={t('common:nav.ideasTooltip')}
          />
        </SidebarLayout.Section>
      </SidebarLayout.Sidebar>
      <SidebarLayout.Content>{children}</SidebarLayout.Content>
      <AppToaster sidebarExpanded={Boolean(openDrawer || openMdDrawer)} />
    </SidebarLayout>
  );
}

AppFrame.propTypes = {
  children: PropTypes.node,
  t: PropTypes.func,
};

export default withTranslation()(AppFrame);
